import type {SettingsKey} from '@/components/settings/injectionKeys';

/**
 * The settings tabs the current user may open, grouped by heading: heading -> [tab, icon][].
 * Shared by the settings dialog & anything else that offers to open a tab (e.g. the spotlight search).
 */
export function useSettingsTabs() {
  const userStore = useUserStore();
  const performanceStore = usePerformanceStore();

  const settingsList = computed<Map<string | undefined, [SettingsKey, string][]>>(() => {
    // For legibility's sake.
    const loginPolicy = userStore.hasAnyPermission(['login-policy:read', 'login-policy:read']);

    const accountItems: [SettingsKey, string][] = [
      ['Profile', 'account-circle'],
      ['Notifications', 'notifications-outline'],
    ];
    const userManagementItems: [SettingsKey, string][] = [];
    const customerItems: [SettingsKey, string][] = [];

    if (userStore.isUserManager) {
      userManagementItems.push(['Users', 'person-outline']);
      userManagementItems.push(['Roles', 'verified-user']);
    }
    if (userStore.isEkcoUserManager) {
      userManagementItems.push(['Ekco Users', 'group-outline']);
    }

    customerItems.push(['Contacts', 'contacts-outline']);
    if (userStore.isUserManager || userStore.isCustomerProductManager)
      customerItems.push(['Products', 'order-approve']);
    if (userStore.isCustomerMappingManager) customerItems.push(['Sources', 'graph-2']);
    if (loginPolicy) customerItems.push(['Login Policy', 'domain-verification-outline']);
    if (userStore.isEkcoUserManager) customerItems.push(['Demo Customers', 'storefront-outline']);

    const entries: [string | undefined, [SettingsKey, string][]][] = [
      ['Account', accountItems],
      ['User Management', userManagementItems],
      ['Customer', customerItems],
      ['Help', [['Feedback', 'feedback-outline']]],
    ];
    // Only the dev backend reports on its performance, see the performance store
    if (performanceStore.entries.length > 0) {
      entries.push(['Developer', [['Performance', 'speed-outline']]]);
    }
    // A heading without any tab beneath it is of no use to the user
    return new Map<string | undefined, [SettingsKey, string][]>(
      entries.filter(([, tabs]) => tabs.length > 0),
    );
  });

  return {settingsList};
}
